import { isEmbeddedAuth } from "~/utils/helpers/auth/browser-context";
import { defineStore } from "pinia";
import {
  TrainingPlanApi,
  TrainingPlanDetailsItemTypeEnum,
  type TrainingPlanDetails,
  SessionsApi,
  TagsApi,
  TagCategoryEnum,
  type TagDetails,
  type AssignTagDto,
  BookingsApi,
  type CreateBookingsDto,
  SessionDetailsApi,
  type GetAttendeesMailInfoDto,
  ProjectsApi,
  type UpdateSessionDto,
  CompanyCatalogApi,
  CatalogStatusEnum,
} from "@simbelapp/order-sdk";
import { ItemType as SdkItemType } from "@simbelapp/order-sdk";
import { useCreateProjectStore } from "../project/create-project.store";
import { useSessionDetailsTimeslotsStore } from "./session-details-timeslots-store";
import type { ISessionsDetailsStore } from "~/utils/interfaces/sessions-interfaces";

import { useFeedback } from "~/composables/feedback/useFeedback";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import {
  AttendeesConfirmEnum,
  AttendeesPendingEnum,
  FAKE_SESSION_ID,
  SessionAttendeesBucketEnum,
  SessionDetailsTabNamesEnum,
  SessionStatusEnum,
} from "~/utils/enums/session-details-enums";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { BookingStatusEnum } from "~/utils/enums/bookings-events.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { usePagination } from "~/composables/paginations/usePagination";
import { useFilters } from "~/composables/filters/useFilters";
import { useExternalCostsStore } from "~/store/external-costs/external-costs.store";
import { useSessionDetailsNotificationsStore } from "~/store/session-details-notifications/session-details-notifications.store";
import { getErrorStatus, isOutOfReachStatus } from "~/utils/helpers/httpErrors";
import { isTrainerOnlyView } from "~/utils/helpers/trainer-activities";
import { useAuthStore } from "~/store/auth/auth.store";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { useSessionDetailsDocumentsStore } from "~/store/sessions/session-details-documents-store";
import { useSessionDetailsFinancingStore } from "~/store/sessions/session-details-financing-store";
import { useSessionDetailsHabilitationsStore } from "~/store/sessions/session-details-habilitations-store";
import { useSettingsStore } from "~/store/settings/settings.store";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { exceedsCapacity, followsDoceboRules } from "~/utils/helpers/sessions/session-capacity";

/** Error rethrown by `fetchSessionDetails`, carrying the HTTP status for its callers. */
type SessionFetchError = Error & { status?: number };

const isTrainerOnlySession = (type: string, details: TrainingPlanDetails | null): boolean =>
  type === ItemType.SESSION && useAuthStore().isInternalTrainer && isTrainerOnlyView(details);

export const useSessionDetailsStore = defineStore("sessionDetailsStore", {
  state: (): ISessionsDetailsStore => {
    return {
      sessionDetails: null,
      tmpGroupsAssigned: [],
      tmpLegalEntity: null,
      sessionDetailsAttendeesBasic: null,
      sessionDetailsAttendeesBasicData: null,
      sessionDetailsAttendeesWithPrices: null,
      sessionDetailsAttendeesWithMail: null,
      currentModalConfirmIndex: null,
      showDeclareAttendances: {
        stepDeclaration: null,
        endTraining: false,
        showModal: false,
        endTrainingSessionModalConfirmation: false,
        showRemainingTimeslotsToEnd: false,
      },
      showEndNoAttendeeModal: false,
      showNotificationModalConfirm: false,
      totalAttendees: null,
      showEditSessionModal: false,
      showAddAttendeeModal: false,
      showConfirmUserSession: false,
      confirmInviteUserSession: {
        notificationType: BookingStatusEnum.BOOKING_CONFIRMED,
        comment: null,
      },
      sessionTraining: null,
      endTrainingSession: {
        showFileUpload: false,
        showHabilitation: false,
      },
      sessionViewModal: false,
      todoListModal: false,
      deleteSession: {
        showModal: false,
      },
      trainingTag: null,
      sessionTag: null,
      tmpTrainingTag: null,
      tmpSessionTag: null,
      isLms: false,
      lmsRules: null,
      sessionSlotsCalendarSync: {
        isLoading: false,
        isError: false,
      },
      // Tab management
      currentTab: null,
      tabPreSelected: null,
      tabsInitialized: {},
    };
  },
  getters: {
    isTrainingStatusDraft(): boolean {
      return this.sessionTraining?.status === CatalogStatusEnum.Draft;
    },
    /** Docebo refuses any overbooking: the session cannot keep a capacity below the attendees already booked. */
    attendeesOverCapacity(): boolean {
      return (
        followsDoceboRules(this.sessionDetails?.lmsProvider) &&
        exceedsCapacity(this.sessionDetails?.nbAttendees, 0, this.sessionDetails?.nbMaxAttendees)
      );
    },
  },
  actions: {
    showSessionDocumentsUpload() {
      this.sessionViewModal = true;
      this.endTrainingSession.showFileUpload = true;
    },
    async showMySession(id: string, type: ItemType) {
      const router = useRouter();
      const sessionsStore = useSessionsStore();
      const feedback = useFeedback();
      const sessionDetailsTimeslotsStore = useSessionDetailsTimeslotsStore();
      // store CORE project
      const createProjectCoreStore = useCreateProjectStore();

      try {
        if (id && type) {
          await this.fetchSessionDetails(id, type as string, { silentIfOutOfReach: true });

          // Embedded sessions live in memory and must survive this internal navigation.
          if (isTrainerOnlySession(type, this.sessionDetails)) {
            await navigateTo(`/trainer-activities/${id}`, { replace: true, external: !isEmbeddedAuth() });
            return;
          }

          await sessionDetailsTimeslotsStore.fetchSessionTimeslots();
          if (this.sessionDetails && type === ItemType.PROJECT) {
            await createProjectCoreStore.processCompleteSessionDetail(id);
          }

          /*
           * This push REPLACES the query, so every deep-linked param has to be carried over or it is
           * dropped before the page can read it: a lost `?tab=` is how the inscription panel lands
           * you on Évaluations, a lost `?attendanceRunId=` loses an émargement being corrected.
           */
          await router.push({ query: { ...router.currentRoute.value.query, id, type } });
        }

        sessionsStore.showSessionModal = false;
        this.sessionViewModal = true;

        /** WIP: SESSION ALERTS COUNT */
        // if (id && type === ItemType.SESSION) {
        //   alertsStore.fetchSessionAlertsCount(id);
        // }
      } catch (error: unknown) {
        // A route middleware turned the push above into its error page — auth.global's « Vos droits
        // ont été modifiés », or a route the new roles no longer open — which Nuxt has already shown.
        // Its 403 is about the route, not the session: read as out of reach, it sent the user on to
        // the not-found page, which replaced the explanation with a screen blaming the session.
        if (isNuxtError(error)) return;

        if (isOutOfReachStatus(getErrorStatus(error))) {
          // Outside an iframe, force a real navigation. A client-side push from here runs while the
          // calling page is still inside its own onMounted, and Suspense keeps the old branch
          // mounted: the URL flips to /sessions/not-found but the empty Sessions list stays on
          // screen. This is a terminal error page, so a full load costs nothing.
          await navigateTo("/sessions/not-found", { replace: true, external: !isEmbeddedAuth() });
          return;
        }

        console.error("Erreur lors de l'affichage de la session:", error);
        feedback.error(`Impossible d'afficher les détails de la session`, "small");
        throw error;
      }
    },
    /**
     * Fetch session details from API
     * @param item_id - Session or project ID
     * @param type - Item type (SESSION or PROJECT)
     * @param options.silentIfOutOfReach - Only for a caller that answers an out-of-reach session
     *   with the not-found page: the toast would land on top of it as a second error. Callers that
     *   refresh an already-open session keep their feedback, or they fail with no visible outcome.
     */
    async fetchSessionDetails(itemId: string, type: string, options?: { silentIfOutOfReach?: boolean }) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);

      try {
        const data: TrainingPlanDetails = await apiInstance.getTrainingPlanDetails({
          itemId,
          type,
        });
        this.sessionDetails = data;
        if (data.tags) {
          this.trainingTag = data.tags?.filter((t) => t.category === TagCategoryEnum.TrainingTag) ?? null;
          this.sessionTag = data.tags?.find((t) => t.category === TagCategoryEnum.SessionTag) ?? null;
        }
        // `showMySession` sends it to the trainer view: the admin fetches below would be refused (not-found page).
        if (isTrainerOnlySession(type, data)) return;
        await Promise.all([
          data.isExternalLms ? this.fetchSessionLmsRules() : Promise.resolve(),
          this.sessionDetails?.trainingId ? this.fetchSessionTrainingDetails() : Promise.resolve(),
        ]);

        if (!this.sessionDetails) {
          feedback.error(`Une erreur est survenue`, "small");
        }
      } catch (error: unknown) {
        const status = getErrorStatus(error);
        if (!options?.silentIfOutOfReach || !isOutOfReachStatus(status)) {
          console.error("Erreur lors de la récupération des détails de la session:", error);
          feedback.error(`Une erreur est survenue lors de la récupération des détails`, "small");
        }
        const err: SessionFetchError = new Error((error as Error)?.message ?? "Session fetch failed");
        err.status = status;
        throw err;
      }
    },
    async fetchSessionLmsRules() {
      if (!this.sessionDetails?.sessionId || !this.sessionDetails?.isExternalLms) {
        this.lmsRules = null;
        return;
      }
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
        this.lmsRules = await apiInstance.getSessionLmsRules({
          sessionId: this.sessionDetails.sessionId,
        });
      } catch (error) {
        console.error("Error fetching LMS rules:", error);
        this.lmsRules = null;
      }
    },
    async fetchAttendeeGlobalDetails(itemId: string, type: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
        const data = await apiInstance.getAttendeeGlobalDetails({ itemId, type });
        // The attendee endpoint returns a narrower model; the my-session subtree only reads the
        // fields it does carry (itemType, userAdmins, location, electronicSignatureOpened).
        this.sessionDetails = data as TrainingPlanDetails;
      } catch (error) {
        console.error("Erreur lors de la récupération des détails attendee:", error);
        feedback.error(`Une erreur est survenue lors de la récupération des détails`, "small");
        throw error;
      }
    },
    async fetchSessionTrainingDetails() {
      const feedback = useFeedback();
      try {
        if (this.sessionDetails?.trainingId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
          const catalog = await apiInstance.getCompanyCatalog({
            trainingId: this.sessionDetails.trainingId,
          });

          this.sessionTraining = catalog.data[0] ?? null;
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des détails de la formation:", error);
        feedback.error(`Une erreur est survenue lors de la récupération des détails de la formation`, "small");
        throw error;
      }
    },
    async fetchSessionAttendeesBasic(
      itemId: string,
      itemType: ItemType | TrainingPlanDetailsItemTypeEnum,
      paginationOptions?: Record<string, string>,
    ) {
      let transformQuery = {};

      if (paginationOptions) {
        transformQuery = Object.fromEntries(new URLSearchParams(paginationOptions).entries());
      } else {
        transformQuery = {
          noLimit: true,
        };
      }

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      try {
        const data = await apiInstance.getBasicAttendees({
          itemId,
          itemType,
          ...transformQuery,
        });
        if (data) {
          this.sessionDetailsAttendeesBasic = {
            data: data?.data?.filter((a) => a.sessionId !== FAKE_SESSION_ID),
            meta: data.meta,
          };
        }

        // Rebuilt on every read, so an emptied list clears the buckets left by the previous one.
        const attendees = this.sessionDetailsAttendeesBasic?.data ?? [];
        this.totalAttendees = {
          [SessionAttendeesBucketEnum.ALL]: attendees,
          [SessionAttendeesBucketEnum.ABSENT]: attendees.filter((a) => a.status === SessionStatusEnum.NO_SHOW),
          [SessionAttendeesBucketEnum.REGISTERED]: attendees.filter((a) =>
            Object.values(AttendeesConfirmEnum).includes(a.status as AttendeesConfirmEnum),
          ),
          [SessionAttendeesBucketEnum.PENDING]: attendees.filter((a) =>
            Object.values(AttendeesPendingEnum).includes(a.status as AttendeesPendingEnum),
          ),
        };
      } catch {
        this.totalAttendees = null;
        this.sessionDetailsAttendeesBasic = null;
      }
    },

    async fetchSessionAttendeesWithMail(getAttendeesMailInfoDto: GetAttendeesMailInfoDto = {}) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      try {
        if (this.sessionDetails?.sessionId) {
          const data = await apiInstance.getAttendeesMailInfo({
            sessionId: this.sessionDetails.sessionId,
            getAttendeesMailInfoDto,
          });
          if (data) {
            this.sessionDetailsAttendeesWithMail = data;
          }
        }
      } catch {
        this.sessionDetailsAttendeesWithMail = null;
      }
    },
    async updateReferent(projectId: string, referentIds: string[], type: TrainingPlanDetailsItemTypeEnum) {
      const feedback = useFeedback();

      try {
        if (projectId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
          await apiInstance.updateTrainingPlanAdmins({
            updateTrainingPlanAdminsDto: {
              userIds: referentIds,
              id: projectId,
              type,
            },
          });

          return true;
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la modification`, "small");
      }
    },

    async updateSession(sessionId: string, updateSessionDto: UpdateSessionDto) {
      const feedback = useFeedback();

      try {
        if (sessionId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
          await apiInstance.updateSession({ sessionId, updateSessionDto });
          await this.fetchSessionDetails(sessionId, ItemType.SESSION);
          feedback.success(`Vos modifications ont été sauvegardées`, "small");
        }
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    async endSessionWithoutAttendees(): Promise<boolean> {
      const feedback = useFeedback();
      if (!this.sessionDetails?.sessionId) return false;
      const itemId = this.sessionDetails.id;
      const itemType = this.sessionDetails.itemType;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
        await apiInstance.endSessionWithoutAttendees({
          sessionId: this.sessionDetails.sessionId,
        });
        if (itemId && itemType) {
          await this.fetchSessionDetails(itemId, itemType);
        }
        feedback.success(`On est tout bon !`, "small");
        return true;
      } catch {
        feedback.error(`Une erreur est survenue lors de la clôture de la session`, "small");
        return false;
      }
    },

    // Update session comment
    async updateSessionComment(itemId: string, type: ItemType, comment: string) {
      const feedback = useFeedback();
      try {
        if (itemId) {
          switch (type) {
            case ItemType.SESSION: {
              const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
              await apiInstance.updateSessionComment({ sessionId: itemId, updateSessionCommentDto: { comment } });
              break;
            }
            case ItemType.PROJECT: {
              const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
              await apiInstance.updateProjectComment({ projectId: itemId, updateProjectCommentDto: { comment } });
              break;
            }
          }
        }
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async assignTagsToItems(
      companyId: string,
      tagType: TagCategoryEnum,
      itemType: SdkItemType,
      tags?: TagDetails[] | TagDetails | null,
      itemIds?: string[],
    ) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
      // assign en masse
      if (itemIds?.length) {
        const assignTagDto: AssignTagDto[] = [];
        for (const id of itemIds) {
          if (Array.isArray(tags) && tags.length) {
            for (const tag of tags) {
              assignTagDto.push({
                itemId: id,
                itemType,
                tagId: tag.tagId ?? undefined,
                category: tagType,
                name: tag?.name,
              });
            }
            // if session tag = tags = {}
          } else if (!Array.isArray(tags)) {
            assignTagDto.push({
              itemId: id,
              itemType,
              tagId: tags?.tagId ?? undefined,
              category: tagType,
              name: undefined,
            });
          } else {
            // remove assign tag
            assignTagDto.push({
              itemId: id,
              itemType,
              tagId: undefined,
              category: tagType,
              name: undefined,
            });
          }
        }
        await apiInstance.assignTagsToItems({
          companyId,
          assignTagDto,
        });
        // simple assign
      } else if (this.sessionDetails) {
        // if training tag = tags[]
        if (Array.isArray(tags) && tags.length) {
          const assignTagDto: AssignTagDto[] = [];
          for (const tag of tags) {
            assignTagDto.push({
              itemId: this.sessionDetails.id,
              itemType,
              tagId: tag.tagId ?? undefined,
              category: tagType,
              name: tag?.name,
            });
          }
          await apiInstance.assignTagsToItems({
            companyId,
            assignTagDto,
          });
          // if session tag = tags = {}
        } else if (!Array.isArray(tags)) {
          await apiInstance.assignTagsToItems({
            companyId,
            assignTagDto: [
              {
                itemId: this.sessionDetails.id,
                itemType,
                tagId: tags?.tagId ?? undefined,
                category: tagType,
                name: tags?.name,
              },
            ],
          });
        } else {
          await apiInstance.assignTagsToItems({
            companyId,
            assignTagDto: [
              {
                itemId: this.sessionDetails.id,
                itemType,
                tagId: undefined,
                category: tagType,
                name: undefined,
              },
            ],
          });
        }
      }
    },
    async batchAssignTrainingTags(
      selectAll: boolean,
      itemType: SdkItemType,
      tagIds: string[],
      tagCategory: TagCategoryEnum,
      itemIds?: string[],
    ) {
      const pagination = usePagination();
      const filters = useFilters();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const queryString = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
      const filtersQuery = filters.getFiltersForSDK();
      try {
        await apiInstance.batchAssignTpTags({
          batchAssignTagsDto: {
            selectAll,
            tagIds,
            itemIds: itemIds ?? [],
            tagCategory,
            itemType,
          },
          ...queryString,
          ...filtersQuery,
        });
      } catch {
        // feedback.error(`Une erreur est survenue`, "small");
      }
    },
    async assignGroupsAndOverwrite(itemId: string, itemType: SdkItemType): Promise<void> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
      const groupIds = this.sessionDetails?.groups?.map((g) => g.groupId) ?? [];
      try {
        await apiInstance.assignGroupsAndOverwrite({
          assignGroupsDto: {
            itemId,
            groupIds,
            itemType,
          },
        });
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    initTmpSessionValues(): void {
      this.tmpGroupsAssigned = [...(this.sessionDetails?.groups ?? [])];

      if (this.sessionDetails?.legalEntityId && this.sessionDetails?.legalEntityName) {
        this.tmpLegalEntity = {
          companyId: this.sessionDetails.legalEntityId,
          name: this.sessionDetails.legalEntityName,
        };
      } else {
        this.tmpLegalEntity = null;
      }
    },

    saveTmpSessionValues(): void {
      if (this.sessionDetails) {
        this.sessionDetails.groups = [...this.tmpGroupsAssigned];
      }
      if (this.sessionDetails && this.tmpLegalEntity) {
        this.sessionDetails.legalEntityId = this.tmpLegalEntity.companyId;
        this.sessionDetails.legalEntityName = this.tmpLegalEntity.name;
      }
    },

    clearTmpSessionValues(): void {
      this.tmpGroupsAssigned = [];
      this.tmpLegalEntity = null;
    },
    /**
     * Create bookings for attendees
     * @param bookings - Booking creation data
     * @returns Promise with booking creation result or error
     */
    async createBookings(bookings: CreateBookingsDto) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        const createBookings = await apiInstance.createBooking({
          createBookingsDto: bookings,
        });
        return { data: createBookings };
      } catch (error: any) {
        return { error };
      }
    },
    handleCloseSessionDetails(closeModalCallback?: Function) {
      const router = useRouter();
      const externalCostsStore = useExternalCostsStore();
      const settingsStore = useSettingsStore();
      settingsStore.resetSettings();
      const sessionDetailsNotificationsStore = useSessionDetailsNotificationsStore();
      const sessionDetailsTimeslotsStore = useSessionDetailsTimeslotsStore();
      const sessionDetailsDocumentsStore = useSessionDetailsDocumentsStore();
      const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
      const sessionDetailsFinancingStore = useSessionDetailsFinancingStore();
      const sessionDetailsHabilitationsStore = useSessionDetailsHabilitationsStore();
      const { resetSessionIndividual } = useSessionIndividual();
      externalCostsStore.$reset();
      router.push({ query: {} });

      if (closeModalCallback) {
        closeModalCallback();
      }

      this.$reset();
      sessionDetailsTimeslotsStore.$reset();
      sessionDetailsNotificationsStore.$reset();
      sessionDetailsDocumentsStore.$reset();
      sessionDetailsAttendeesStore.$reset();
      sessionDetailsFinancingStore.$reset();
      sessionDetailsHabilitationsStore.$reset();
      resetSessionIndividual();
    },

    /**
     * Tab Management Actions
     */

    /**
     * Set the pre-selected tab (for programmatic navigation)
     */
    setTabPreSelected(tabName: string | null) {
      this.tabPreSelected = tabName;
    },

    /**
     * Initialize data for a specific tab (lazy loading)
     * This method orchestrates the initialization of different stores
     */
    async initializeTabData(tabName: string) {
      if (!this.sessionDetails?.id) {
        console.warn("Cannot initialize tab data without session details");
        return;
      }

      try {
        switch (tabName) {
          case SessionDetailsTabNamesEnum.PARTICIPANTS: {
            // Initialize participant data

            const attendeesStore = useSessionDetailsAttendeesStore();
            const paginationOptions = usePagination(PaginationKeysEnum.ATTENDEES_PAGINATION);
            const paginateUnscheduleUsers = usePagination(PaginationKeysEnum.UNSCHEDULE_USERS_PAGINATION);

            const paginationOptionsAttendees = paginationOptions.getFindOptions(
              paginationOptions.paginateOptions.value,
            );
            const paginationOptionsUnscheduleUsers = paginateUnscheduleUsers.getFindOptions(
              paginateUnscheduleUsers.paginateOptions.value,
            );

            await attendeesStore.fetchSessionAttendeesFullDatas(
              this.sessionDetails.id,
              this.sessionDetails.itemType,
              paginationOptionsAttendees,
            );
            await attendeesStore.fetchSessionUnscheduleUsers(
              this.sessionDetails.id,
              this.sessionDetails.itemType,
              paginationOptionsUnscheduleUsers,
            );
            break;
          }

          case SessionDetailsTabNamesEnum.ADMIN: {
            // Refresh session details for admin
            await this.fetchSessionDetails(this.sessionDetails.id, this.sessionDetails.itemType);
            await this.fetchSessionAttendeesBasic(this.sessionDetails.id, this.sessionDetails.itemType);
            break;
          }

          default:
            console.warn(`Unknown tab: ${tabName}`);
        }
      } catch (error) {
        console.error(`Error initializing tab ${tabName}:`, error);
        throw error;
      }
    },

    /**
     * Navigate to a tab and initialize its data if necessary
     */
    async navigateToTab(tabName: string) {
      this.currentTab = tabName;
      await this.initializeTabData(tabName);
    },

    /**
     * Reset current tab data
     * Useful after modifications (adding participant, etc.)
     */
    async refreshCurrentTabData() {
      if (!this.currentTab) return;
      if (!this.sessionDetails?.id) return;

      await this.fetchSessionAttendeesBasic(this.sessionDetails.id, this.sessionDetails.itemType);
      try {
        switch (this.currentTab) {
          case SessionDetailsTabNamesEnum.PARTICIPANTS: {
            const attendeesStore = useSessionDetailsAttendeesStore();
            const paginationOptions = usePagination(PaginationKeysEnum.ATTENDEES_PAGINATION);
            const paginateUnscheduleUsers = usePagination(PaginationKeysEnum.UNSCHEDULE_USERS_PAGINATION);

            const paginationOptionsAttendees = paginationOptions.getFindOptions(
              paginationOptions.paginateOptions.value,
            );
            const paginationOptionsUnscheduleUsers = paginateUnscheduleUsers.getFindOptions(
              paginateUnscheduleUsers.paginateOptions.value,
            );

            await attendeesStore.fetchSessionAttendeesFullDatas(
              this.sessionDetails.id,
              this.sessionDetails.itemType,
              paginationOptionsAttendees,
            );
            await attendeesStore.fetchSessionUnscheduleUsers(
              this.sessionDetails.id,
              this.sessionDetails.itemType,
              paginationOptionsUnscheduleUsers,
            );
            break;
          }

          case SessionDetailsTabNamesEnum.DOCUMENTS: {
            const { useSessionDetailsDocumentsStore } =
              await import("~/store/sessions/session-details-documents-store");
            const documentsStore = useSessionDetailsDocumentsStore();
            await documentsStore.fetchSessionDocumentsWithPagination(this.sessionDetails.id);
            break;
          }

          // Add other cases if necessary
          default:
            console.log(`No refresh handler for tab: ${this.currentTab}`);
        }
      } catch (error) {
        console.error(`Error refreshing tab ${this.currentTab}:`, error);
      }
    },
    async updateCommentTraining(itemid: string, itemType: ItemType, trainingComment: string | null) {
      const feedback = useFeedback();
      try {
        if (itemid) {
          if (itemType === ItemType.PROJECT) {
            const apiProjectsInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
            await apiProjectsInstance.updateProjectTrainingComment({
              projectId: itemid,
              updateProjectTrainingCommentDto: {
                trainingComment: trainingComment ?? "",
              },
            });
          } else {
            const apiSessionsInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
            await apiSessionsInstance.updateSessionTrainingComment({
              sessionId: itemid,
              updateSessionTrainingCommentDto: {
                trainingComment: trainingComment ?? "",
              },
            });
          }
        }
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
  },
});
